<template>
  <q-item
    v-bind="{ clickable, disable, dense, active, activeClass, tag, dark, insetLevel, tabindex, manualFocus, focused }"
    @click="$emit('click')"
  >
    <slot name="before" />

    <q-item-section
      v-if="icon"
      side
      :top="top"
      v-bind="iconSectionProps"
    >
      <q-icon
        :name="icon"
        :color="color"
        :style="iconStyle"
        :size="iconSize"
        :class="iconClass"
      />
    </q-item-section>

    <q-item-section
      :class="{['text-' + color]: !!color, 'reverse': reverse}"
      :style="mainStyle"
      :side="side"
    >
      <q-item-label
        v-if="label"
        :class="labelClass"
        :style="labelStyle"
        :title="labelTitle"
        v-bind="labelProps"
      >
        {{ label }}
        <span v-if="required" class="required-astrx" />
      </q-item-label>

      <slot />

      <q-item-label
        v-if="caption"
        caption
        :class="captionClass"
        :style="captionStyle"
        v-bind="captionProps"
      >
        {{ caption }}
      </q-item-label>
    </q-item-section>

    <slot name="after" />

    <q-item-section
      v-if="iconRight"
      side
      :top="top"
    >
      <q-icon
        :name="iconRight"
        :color="color"
        :style="iconStyle"
        :size="iconSize"
        :class="iconClass"
      />
    </q-item-section>
  </q-item>
</template>

<script>
import itemMixin from 'src/helpers/item'

/**
 * A basic standard implementation of QItem for simple QItems
 *
 * EMIT:
 * - click
 *
 * SLOTS:
 * - default: default section slot
 * - before: bind anything before the main content section (hides the default icon implementation)
 * - after: bind anything after the main content section
 */
export default {
  mixins: [itemMixin],
  props: {
    top: {
      type: Boolean,
      default: false
    },
    // Style to apply to main section
    mainStyle: {
      type: [String, Object]
    },
    // Icon / text color
    color: {
      type: String
    },
    // Side section icon
    icon: {
      type: String
    },
    // Icon on right of main section
    iconRight: {
      type: String
    },
    // Style to apply to the icon
    iconStyle: {
      type: [String, Object]
    },
    // Apply size to icon
    iconSize: {
      type: [String, Number]
    },
    iconClass: {
      type: [String, Object]
    },
    // Main section label
    label: {
      type: [String, Number]
    },
    // Title to apply to label when hovered over
    labelTitle: {
      type: String
    },
    // Class to apply to the label
    labelClass: {
      type: [String, Object]
    },
    // Style to apply to the label
    labelStyle: {
      type: [String, Object]
    },
    // Main section caption
    caption: {
      type: [String, Number]
    },
    // Class to apply to the caption
    captionClass: {
      type: [String, Object]
    },
    // Style to apply to the caption
    captionStyle: {
      type: [String, Object]
    },
    // Is item required
    required: {
      type: Boolean,
      default: false
    },
    // Flips the caption and label values
    reverse: {
      type: Boolean,
      default: false
    },
    // Force main content area to use side param
    side: {
      type: Boolean,
      default: false
    },
    captionProps: {
      type: Object,
      default: () => {
        return {}
      }
    },
    labelProps: {
      type: Object,
      default: () => {
        return {}
      }
    },
    iconSectionProps: {
      type: Object,
      default: () => {
        return {}
      }
    }
  }
}
</script>
